import { useMemo, useState } from 'react';
import { Search, Star, MapPin, User } from 'lucide-react';
import { useCoaches, type CoachFilters } from '../../hooks/useAPI';
import { RowSkeleton } from './Skeleton';
import FavoriteButton from './FavoriteButton';
import { FilterPanel, FilterPillRow } from './FilterPills';

interface CoachesScreenProps {
  onNavigate: (screen: any, params?: any) => void;
}

const LESSON_TYPES: ReadonlyArray<{ value: 'any' | 'private' | 'semi_private' | 'group'; label: string }> = [
  { value: 'any',           label: 'All' },
  { value: 'private',       label: 'Private' },
  { value: 'semi_private',  label: 'Semi-private' },
  { value: 'group',         label: 'Group' },
];

type PriceFilter = 'any' | 'lt100' | '100-200' | 'gt200';
const PRICE_OPTIONS: ReadonlyArray<{ value: PriceFilter; label: string; matches: (price: number | null) => boolean }> = [
  { value: 'any',     label: 'Any price',         matches: () => true },
  { value: 'lt100',   label: 'Under AED 100',     matches: (p) => p != null && p < 100 },
  { value: '100-200', label: 'AED 100–200',       matches: (p) => p != null && p >= 100 && p <= 200 },
  { value: 'gt200',   label: 'AED 200+',          matches: (p) => p != null && p > 200 },
];

type ExperienceFilter = 'any' | '3+' | '5+' | '10+';
const EXPERIENCE_OPTIONS: ReadonlyArray<{ value: ExperienceFilter; label: string; min: number }> = [
  { value: 'any',  label: 'Any experience', min: 0 },
  { value: '3+',   label: '3+ years',       min: 3 },
  { value: '5+',   label: '5+ years',       min: 5 },
  { value: '10+',  label: '10+ years',      min: 10 },
];

type GenderFilter = 'any' | 'male' | 'female';
const GENDER_OPTIONS: ReadonlyArray<{ value: GenderFilter; label: string }> = [
  { value: 'any',    label: 'Any coach' },
  { value: 'male',   label: 'Male' },
  { value: 'female', label: 'Female' },
];

export default function CoachesScreen({ onNavigate }: CoachesScreenProps) {
  const [lessonType, setLessonType] = useState<'any' | 'private' | 'semi_private' | 'group'>('any');
  const [price, setPrice] = useState<PriceFilter>('any');
  const [experience, setExperience] = useState<ExperienceFilter>('any');
  const [gender, setGender] = useState<GenderFilter>('any');
  const [q, setQ] = useState('');

  // Pass lessonType to the hook so its `min_price` reflects the
  // chosen lesson type — that way "Private + Under AED 100" filters
  // on the private rate, not the cheapest rate (which might be
  // group). The other filters are applied client-side below.
  const filters: CoachFilters = lessonType === 'any' ? {} : { lessonType };
  const { data: coaches, isLoading } = useCoaches(filters);

  const rows = useMemo(() => {
    const list = (coaches as any[]) || [];
    const search = q.trim().toLowerCase();
    const expMin = EXPERIENCE_OPTIONS.find((e) => e.value === experience)!.min;
    const priceMatcher = PRICE_OPTIONS.find((p) => p.value === price)!.matches;
    return list.filter((c) => {
      if (search) {
        const haystack = [
          c.display_name,
          c.bio,
          (c.specialties ?? []).join(' '),
          (c.languages ?? []).join(' '),
          c.clubs?.name,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      if ((c.years_experience ?? 0) < expMin) return false;
      if (gender !== 'any' && c.gender !== gender) return false;
      if (!priceMatcher(c.min_price ?? null)) return false;
      return true;
    });
  }, [coaches, q, experience, price, gender]);

  return (
    <div className="w-full min-h-screen bg-background pb-24">
      <div className="bg-gradient-to-br from-[#0F1E36] to-[#16294A] text-white px-6 pt-6 pb-6 rounded-b-[2rem]">
        <h1 className="text-2xl mb-1">Find a coach</h1>
        <p className="text-sm opacity-80">
          Verified pros across Dubai — private, semi-private, or group.
        </p>
      </div>

      <div className="px-6 -mt-4 mb-4">
        <div className="bg-card border border-border rounded-2xl p-3 flex items-center gap-2 shadow-sm">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, language, specialty"
            className="flex-1 bg-transparent text-sm outline-none"
          />
        </div>
      </div>

      <div className="px-6 mb-4">
        <FilterPanel
          activeCount={
            (lessonType !== 'any' ? 1 : 0)
            + (price      !== 'any' ? 1 : 0)
            + (experience !== 'any' ? 1 : 0)
            + (gender     !== 'any' ? 1 : 0)
          }
          onClearAll={() => {
            setLessonType('any');
            setPrice('any');
            setExperience('any');
            setGender('any');
          }}
        >
          <FilterPillRow
            label="Lesson type"
            defaultValue="any"
            options={LESSON_TYPES}
            value={lessonType}
            onChange={(v) => setLessonType(v as typeof lessonType)}
          />
          <FilterPillRow
            label="Price band"
            defaultValue="any"
            options={PRICE_OPTIONS}
            value={price}
            onChange={(v) => setPrice(v as PriceFilter)}
          />
          <FilterPillRow
            label="Experience"
            defaultValue="any"
            options={EXPERIENCE_OPTIONS}
            value={experience}
            onChange={(v) => setExperience(v as ExperienceFilter)}
          />
          <FilterPillRow
            label="Coach"
            defaultValue="any"
            options={GENDER_OPTIONS}
            value={gender}
            onChange={(v) => setGender(v as GenderFilter)}
          />
        </FilterPanel>
      </div>

      <div className="px-6 space-y-3">
        {isLoading ? (
          <>
            <RowSkeleton />
            <RowSkeleton />
            <RowSkeleton />
          </>
        ) : rows.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-12">
            {q.trim() || price !== 'any' || experience !== 'any' || gender !== 'any'
              ? 'No coaches match these filters.'
              : 'No verified coaches available yet.'}
          </div>
        ) : (
          rows.map((c: any) => (
            <button
              key={c.id}
              onClick={() => onNavigate('coach', { id: c.id })}
              className="w-full text-left bg-card border border-border rounded-2xl p-4 flex gap-3 hover:border-[#1A6B4F]/40 transition-colors relative"
            >
              <div className="absolute top-2 right-2">
                <FavoriteButton target={{ kind: 'coach', id: c.id }} variant="icon" />
              </div>
              <div className="w-16 h-16 rounded-2xl bg-secondary overflow-hidden flex-shrink-0 flex items-center justify-center">
                {c.photo_url ? (
                  <img src={c.photo_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-7 h-7 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 min-w-0 pr-8">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{c.display_name}</span>
                  {c.type === 'freelance' && (
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#D4F04A]/20 text-[#1A6B4F]">
                      Freelance
                    </span>
                  )}
                </div>
                {c.bio && (
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{c.bio}</p>
                )}
                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-muted-foreground flex-wrap">
                  {c.years_experience != null && (
                    <span className="flex items-center gap-1">
                      <Star className="w-3 h-3" />
                      {c.years_experience}y exp
                    </span>
                  )}
                  {(c.languages ?? []).length > 0 && (
                    <span className="uppercase tracking-wider">
                      {(c.languages ?? []).slice(0, 3).join(' · ')}
                    </span>
                  )}
                  {c.clubs?.name && (
                    <span className="flex items-center gap-1 truncate">
                      <MapPin className="w-3 h-3" />
                      {c.clubs.name}
                    </span>
                  )}
                </div>
                {c.min_price != null && (
                  <div className="text-xs mt-1.5">
                    <span className="text-[#1A6B4F] font-semibold">
                      From AED {Math.round(Number(c.min_price))}
                    </span>
                    <span className="text-muted-foreground"> / session</span>
                  </div>
                )}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

